/**
 * Distributor Dashboard Page
 * Complete dashboard for distributors with inventory, transfers, and activation controls
 * Combines Tasks 14.1, 14.2, 14.3, and 14.4
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { productService, Product } from '../services/product.service';
import { transferService, Transfer } from '../services/transfer.service';
import { userService, PendingUser } from '../services/user.service';
import { Button, Alert, Modal, Select } from '../components/ui';

type TabType = 'inventory' | 'transfers' | 'approvals';

export default function DistributorDashboard() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  const [activeTab, setActiveTab] = useState<TabType>('inventory');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Inventory state
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  
  // Transfer state
  const [transfers, setTransfers] = useState<{ sent: Transfer[]; received: Transfer[] }>({ sent: [], received: []});
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferProductId, setTransferProductId] = useState<string | null>(null);
  const [childUsers, setChildUsers] = useState<any[]>([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [transferring, setTransferring] = useState(false);
  
  // Approvals state
  const [pendingApprovals, setPendingApprovals] = useState<PendingUser[]>([]);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (activeTab === 'inventory') {
        await loadInventory();
      } else if (activeTab === 'transfers') {
        await loadTransfers();
      } else if (activeTab === 'approvals') {
        await loadApprovals();
      }
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadInventory = async () => {
    if (!user?.manufacturerId) return;
    const inventory = await productService.getManufacturerInventory(user.manufacturerId, {
      // Could add filters here
    });
    setProducts(inventory.filter(p => p.currentOwnerId === user.id));
  };

  const loadTransfers = async () => {
    const transferData = await transferService.getAllTransfers();
    setTransfers(transferData);
  };

  const loadApprovals = async () => {
    const approvals = await userService.getPendingApprovals();
    setPendingApprovals(approvals);
  };

  const loadChildUsers = async () => {
    const children = await userService.getUserChildren();
    setChildUsers(children);
  };

  // Activation controls (Task 14.4)
  const handleActivateProduct = async (productId: string) => {
    try {
      setError(null);
      await productService.activateProduct(productId);
      setSuccess('Product activated successfully!');
      await loadInventory();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to activate product');
    }
  };

  const handleBulkActivate = async () => {
    if (selectedProducts.size === 0) {
      setError('Please select products to activate');
      return;
    }

    try {
      setError(null);
      await productService.bulkActivateProducts(Array.from(selectedProducts));
      setSuccess(`${selectedProducts.size} products activated successfully!`);
      setSelectedProducts(new Set());
      await loadInventory();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to activate products');
    }
  };

  const toggleProductSelection = (productId: string) => {
    const newSelection = new Set(selectedProducts);
    if (newSelection.has(productId)) {
      newSelection.delete(productId);
    } else {
      newSelection.add(productId);
    }
    setSelectedProducts(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  // Transfer interface (Task 14.2)
  const handleTransferClick = async (productId: string) => {
    setTransferProductId(productId);
    setShowTransferModal(true);
    await loadChildUsers();
  };

  const handleTransferSubmit = async () => {
    if (!transferProductId || !selectedRecipient) {
      setError('Please select a recipient');
      return;
    }

    try {
      setTransferring(true);
      setError(null);
      
      await transferService.createTransfer({
        productId: transferProductId,
        toUserId: selectedRecipient
      });
      
      setSuccess('Product transferred successfully!');
      setShowTransferModal(false);
      setTransferProductId(null);
      setSelectedRecipient('');
      await loadInventory();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to transfer product');
    } finally {
      setTransferring(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Distributor Portal</h1>
          <p className="text-sm text-gray-600 mt-1">{user?.fullName}</p>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="error" className="mb-6" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-6" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px" role="tablist" aria-label="Distributor sections">
              <button
                type="button"
                onClick={() => setActiveTab('inventory')}
                role="tab"
                aria-selected={activeTab === 'inventory'}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'inventory'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                My Inventory
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('transfers')}
                role="tab"
                aria-selected={activeTab === 'transfers'}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'transfers'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Transfer History
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('approvals')}
                role="tab"
                aria-selected={activeTab === 'approvals'}
                className={`px-6 py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'approvals'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Approvals
                {pendingApprovals.length > 0 && (
                  <span className="ml-2 bg-orange-100 text-orange-800 text-xs font-medium px-2 py-0.5 rounded-full">
                    {pendingApprovals.length}
                  </span>
                )}
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Loading...</p>
              </div>
            ) : (
              <>
                {/* Inventory Tab (Task 14.1 & 14.4) */}
                {activeTab === 'inventory' && (
                  <div>
                    {selectedProducts.size > 0 && (
                      <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <span className="text-sm text-blue-800">
                          {selectedProducts.size} product(s) selected
                        </span>
                        <Button onClick={handleBulkActivate} size="sm">
                          Activate Selected
                        </Button>
                      </div>
                    )}

                    {products.length === 0 ? (
                      <div className="text-center py-12">
                        <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                        </svg>
                        <p className="text-gray-600">No products in your inventory</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200" aria-label="Distributor inventory">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left">
                                <input
                                  type="checkbox"
                                  checked={selectedProducts.size === products.length && products.length > 0}
                                  onChange={toggleSelectAll}
                                  className="rounded border-gray-300"
                                  aria-label="Select all inventory products"
                                />
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Serial #</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {products.map((product) => (
                              <tr key={product.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4">
                                  <input
                                    type="checkbox"
                                    checked={selectedProducts.has(product.id)}
                                    onChange={() => toggleProductSelection(product.id)}
                                    className="rounded border-gray-300"
                                    aria-label={`Select product serial ${product.serialNumber}`}
                                  />
                                </td>
                                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                                  #{product.serialNumber}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  Product {product.serialNumber}
                                </td>
                                <td className="px-6 py-4">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    product.activationStatus === 'ACTIVATED' ? 'bg-green-100 text-green-800' :
                                    product.activationStatus === 'CONSUMED' ? 'bg-gray-100 text-gray-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                    {product.activationStatus}
                                  </span>
                                </td>
                                <td className="px-6 py-4 text-right text-sm space-x-2">
                                  {product.activationStatus === 'CREATED' && (
                                    <button
                                      onClick={() => handleActivateProduct(product.id)}
                                      className="text-green-600 hover:text-green-900"
                                    >
                                      Activate
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleTransferClick(product.id)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Transfer
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Transfers Tab (Task 14.3) */}
                {activeTab === 'transfers' && (
                  <div className="space-y-6">
                    {/* Sent Transfers */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Sent Transfers</h3>
                      {transfers.sent.length === 0 ? (
                        <p className="text-gray-600 text-center py-8">No sent transfers</p>
                      ) : (
                        <div className="space-y-3">
                          {transfers.sent.map((transfer) => (
                            <div key={transfer.id} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    Product #{transfer.product?.serialNumber}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">
                                    To: {transfer.toUser?.fullName}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {formatDate(transfer.createdAt)}
                                  </p>
                                </div>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  transfer.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                  transfer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {transfer.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Received Transfers */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Received Transfers</h3>
                      {transfers.received.length === 0 ? (
                        <p className="text-gray-600 text-center py-8">No received transfers</p>
                      ) : (
                        <div className="space-y-3">
                          {transfers.received.map((transfer) => (
                            <div key={transfer.id} className="border border-gray-200 rounded-lg p-4">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    Product #{transfer.product?.serialNumber}
                                  </p>
                                  <p className="text-sm text-gray-600 mt-1">
                                    From: {transfer.fromUser?.fullName}
                                  </p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    {formatDate(transfer.createdAt)}
                                  </p>
                                </div>
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  transfer.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                                  transfer.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-red-100 text-red-800'
                                }`}>
                                  {transfer.status}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Approvals Tab */}
                {activeTab === 'approvals' && (
                  <div>
                    {pendingApprovals.length === 0 ? (
                      <div className="text-center py-12">
                        <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-gray-600">No pending approvals</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {pendingApprovals.map((user) => (
                          <div key={user.id} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium text-gray-900">{user.fullName}</p>
                                <p className="text-sm text-gray-600">{user.email}</p>
                                <p className="text-sm text-gray-600">{user.phone}</p>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" onClick={() => navigate('/users/approvals')}>
                                  Review
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

      {/* Transfer Modal (Task 14.2) */}
      {showTransferModal && (
        <Modal
          isOpen={showTransferModal}
          onClose={() => {
            setShowTransferModal(false);
            setTransferProductId(null);
            setSelectedRecipient('');
          }}
          title="Transfer Product"
        >
          <div className="space-y-4">
            <p className="text-gray-600">
              Select a recipient from your network to transfer this product.
            </p>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipient
              </label>
              <Select
                value={selectedRecipient}
                onChange={(e) => setSelectedRecipient(e.target.value)}
                options={[
                  { value: '', label: 'Select recipient...' },
                  ...childUsers.map(child => ({
                    value: child.id,
                    label: `${child.fullName} (${child.email})`
                  }))
                ]}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowTransferModal(false);
                  setTransferProductId(null);
                  setSelectedRecipient('');
                }}
                disabled={transferring}
              >
                Cancel
              </Button>
              <Button
                onClick={handleTransferSubmit}
                disabled={transferring || !selectedRecipient}
              >
                {transferring ? 'Transferring...' : 'Transfer Product'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

